import {
	Alert,
	Button,
	Grid,
	MenuItem,
	Select,
	TextField,
	Typography,
	Paper,
	Container,
	Box
} from "@mui/material";
import React, { useState, useEffect } from "react";
import { getErrorMessage, getFields, requestsToUse, validarNIF } from "../helpers";
import ImageUpload from "./imageUpload";
import { useMutation } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";

const Create = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const location = useLocation();

	// Get the entity type from URL state or search params
	const getEntityType = () => {
		// Check if entity type is in location state
		if (location.state && location.state.entityType) {
			return location.state.entityType;
		}

		// Check URL search params
		const searchParams = new URLSearchParams(location.search);
		const entityType = searchParams.get('type');
		if (entityType) {
			return entityType;
		}

		// Default to clients if no type specified
		return 'clients';
	};

	const entityType = getEntityType();
	const entityLabel = t(`PATH.${entityType}`, entityType);
	const translateField = (fieldName) => t(`FIELDS.${fieldName}`, fieldName);
	const formatFieldList = (items) => {
		if (items.length <= 1) return items.join("");
		if (items.length === 2) return `${items[0]} e ${items[1]}`;
		return `${items.slice(0, -1).join(", ")} e ${items[items.length - 1]}`;
	};

	const fields = getFields(entityType);
	const requestsToUseHere = requestsToUse(entityType);
	const requiredFieldsByEntity = {
		clients: ["name"],
		technicians: ["name", "email", "password"],
	};
	const requiredFields = requiredFieldsByEntity[entityType] || [];

	const [create, { data: dataCreate, error: errorCreate, loading }] = useMutation(
		requestsToUseHere.mutationCreate,
		{
			onError: (error) => {
				console.error('Mutation error:', error.message);
			}
		}
	);

	const [inputs, setInputs] = useState({});
	const [image, setImage] = useState();
	const [nifError, setNifError] = useState(null);
	const [fieldErrors, setFieldErrors] = useState({});

	// Redirect after successful creation
	useEffect(() => {
		if (dataCreate) {
			// Navigate back to the list page
			navigate(`/${entityType}`);
		}
	}, [dataCreate, navigate, entityType]);

	const handleSubmit = () => {
		// Prepare the data for submission
		const formData = { ...inputs };

		const missingRequiredFields = requiredFields.filter((fieldName) => {
			const value = formData[fieldName];
			return typeof value !== "string" || value.trim() === "";
		});

		if (missingRequiredFields.length > 0) {
			const nextFieldErrors = missingRequiredFields.reduce((acc, fieldName) => {
				acc[fieldName] = t("CREATE.FIELD_REQUIRED", {
					field: translateField(fieldName),
				});
				return acc;
			}, {});
			const missingFieldLabels = missingRequiredFields
				.map((fieldName) => translateField(fieldName))
				.filter(Boolean);

			setFieldErrors(nextFieldErrors);
			setSubmissionError(t("CREATE.MISSING_REQUIRED_FIELDS", {
				fields: formatFieldList(missingFieldLabels),
			}));
			return;
		}

		// Basic validation
		if (entityType === 'clients') {
			// Validate NIF if provided
			if (formData.nif && formData.nif.trim() !== '') {
				if (!validarNIF(formData.nif)) {
					setSubmissionError(t("CREATE.INVALID_NIF"));
					setNifError(t("CREATE.INVALID_NIF"));
					return;
				}
			}
		}

		// Handle addressInfo special case - only create if fields have actual values
		if ((formData.Address && formData.Address.trim() !== '') ||
			(formData.postalCode && formData.postalCode.trim() !== '')) {
			formData.addressInfo = {
				Address: formData.Address || '',
				postalCode: formData.postalCode || ''
			};

			// Remove individual address fields to prevent duplication
			delete formData.Address;
			delete formData.postalCode;
		} else {
			// If both are empty, remove them to avoid sending empty object
			delete formData.Address;
			delete formData.postalCode;
		}
		// localidade stays as is (not inside addressInfo)

		// Add the image if available
		if (image) {
			formData.avatar = image;
		}

		// Clear any previous errors
		setSubmissionError(null);
		setFieldErrors({});

		create({
			variables: formData,
			onError: (error) => {
				setSubmissionError(getErrorMessage(error));
			}
		});
	};

	// Add state for submission errors
	const [submissionError, setSubmissionError] = useState(null);

	return (
		<Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
			<Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
				<Box sx={{ mb: 4, textAlign: 'center' }}>
					<Typography variant="h4" component="h1" color="primary" gutterBottom>
						{t("CREATE.TITLE", { entity: entityLabel })}
					</Typography>
					<Typography variant="subtitle1" color="text.secondary">
						{t("CREATE.SUBTITLE", { entity: entityLabel })}
					</Typography>
				</Box>

				<Grid container spacing={3}>
					{fields?.map((f, i) => {
						if (f.type == "image") {
							return (
								<Grid item xs={12} key={i} sx={{ display: 'flex', justifyContent: 'center' }}>
									<ImageUpload cb={setImage} />
								</Grid>
							);
						}

						if (f.type == "string")
							return (
								<Grid item xs={12} sm={6} key={i}>
									<TextField
										fullWidth
										placeholder={translateField(f.fieldName)}
										label={translateField(f.fieldName)}
										variant="outlined"
										type={f.fieldName === "password" ? "password" : "text"}
										required={requiredFields.includes(f.fieldName)}
										error={Boolean(fieldErrors[f.fieldName]) || (f.fieldName === 'nif' && nifError !== null)}
										helperText={fieldErrors[f.fieldName] || (f.fieldName === 'nif' && nifError !== null ? nifError : '')}
										onChange={(ev) => {
											const value = ev.target.value;
											setInputs(prev => ({
												...prev,
												[f.fieldName]: value
											}));
											if (fieldErrors[f.fieldName]) {
												setFieldErrors(prev => {
													const next = { ...prev };
													delete next[f.fieldName];
													return next;
												});
											}

											// Validate NIF in real-time for clients
											if (f.fieldName === 'nif' && entityType === 'clients') {
												if (value.trim() === '') {
													setNifError(null);
												} else if (!validarNIF(value)) {
													setNifError(t("CREATE.INVALID_NIF"));
												} else {
													setNifError(null);
												}
											}
										}}
									/>
								</Grid>
							);

						if (f.type == "select")
							return (
								<Grid item xs={12} sm={6} key={i}>
									<Select
										fullWidth
										labelId={`select-${f.fieldName}-label`}
										id={`select-${f.fieldName}`}
										label={translateField(f.fieldName)}
										defaultValue=""
										displayEmpty
										onChange={(ev) => {
											setInputs(prev => ({
												...prev,
												[f.fieldName]: ev.target.value
											}));
										}}>
										<MenuItem value="" disabled>
											<em>
												{t("CREATE.SELECT_FIELD", {
													field: translateField(f.fieldName),
												})}
											</em>
										</MenuItem>
										{f.selectFields.map((field, idx) => {
											return <MenuItem key={idx} value={field}>{field}</MenuItem>;
										})}
									</Select>
								</Grid>
							);

						if (f.type == "multiple")
							return (
								<Grid item xs={12} key={i}>
									<Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
										<Typography variant="subtitle2" gutterBottom sx={{ mb: 2 }}>
											{f.fieldName
												? translateField(f.fieldName)
												: t("CREATE.ADDITIONAL_DETAILS")}
										</Typography>
										<Grid container spacing={2}>
											{f.fields.map((field, idx) => {
												return (
													<Grid item xs={12} sm={6} md={4} key={idx}>
														<TextField
															fullWidth
															size="small"
															placeholder={translateField(field.fieldName)}
															label={translateField(field.fieldName)}
															onChange={(ev) => {
																setInputs(prev => ({
																	...prev,
																	[field.fieldName]: ev.target.value
																}));
															}}
														/>
													</Grid>
												);
											})}
										</Grid>
									</Paper>
								</Grid>
							);

						return null;
					})}

					{errorCreate && !submissionError && (
						<Grid item xs={12}>
							<Alert severity="error">
								{getErrorMessage(errorCreate) || t("CREATE.CREATE_ERROR")}
							</Alert>
						</Grid>
					)}

					{submissionError && (
						<Grid item xs={12}>
							<Alert severity="error">
								{submissionError}
							</Alert>
						</Grid>
					)}

					<Grid item xs={12} sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
						<Button
							variant="outlined"
							color="inherit"
							onClick={() => navigate(-1)}
							disabled={loading}
						>
							{t("ACTIONS.CANCEL")}
						</Button>
						<Button
							variant="contained"
							color="primary"
							onClick={handleSubmit}
							disabled={loading}
							size="large"
							sx={{ minWidth: 150 }}
						>
							{loading ? t("CREATE.SUBMITTING") : t("CREATE.SUBMIT")}
						</Button>
					</Grid>
				</Grid>
			</Paper>
		</Container>
	);
};

export default Create;
